import { useCallback, useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ProductSourceUploadReviewPanel } from '@/features/product-catalog/components/ProductSourceUploadReviewPanel'
import { ProductSourceUploadDialogInstructions } from '@/features/product-catalog/components/ProductSourceUploadDialogInstructions'
import {
  assessProductSourceUploadEligibilityBatch,
  type ProductSourceUploadEligibility,
} from '@/features/product-catalog/lib/assessProductSourceUploadEligibility'
import {
  isAllowedProductSourceFile,
  PRODUCT_SOURCE_FILE_ACCEPT,
} from '@/features/product-catalog/utils/productSourceFileTypes'

type DialogPhase = 'pick' | 'review'

type AddProductSourcesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onFileSelect: (file: File) => void | Promise<void>
  onMultiFileSelect?: (files: File[]) => void | Promise<void>
}

export function AddProductSourcesDialog({
  open,
  onOpenChange,
  onFileSelect,
  onMultiFileSelect,
}: AddProductSourcesDialogProps) {
  const { t } = useTranslation('productCatalog')
  const [phase, setPhase] = useState<DialogPhase>('pick')
  const [isAssessing, setIsAssessing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [assessments, setAssessments] = useState<ProductSourceUploadEligibility[]>(
    () => []
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetDialog = useCallback(() => {
    setPhase('pick')
    setIsAssessing(false)
    setIsUploading(false)
    setAssessments([])
  }, [])

  useEffect(() => {
    if (!open) {
      resetDialog()
    }
  }, [open, resetDialog])

  const assessAndReviewFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return
      }
      const hasAllowedFile = files.some((file) => isAllowedProductSourceFile(file))
      if (!hasAllowedFile) {
        toast.error(t('sources.addDialog.allowedFileTypes'))
        return
      }
      setPhase('review')
      setIsAssessing(true)
      try {
        const nextAssessments =
          await assessProductSourceUploadEligibilityBatch(files)
        setAssessments(nextAssessments)
      } finally {
        setIsAssessing(false)
      }
    },
    [t]
  )

  const handleConfirmUpload = useCallback(async () => {
    const eligibleFiles = assessments
      .filter((row) => row.eligible)
      .map((row) => row.file)
    if (eligibleFiles.length === 0) {
      return
    }
    setIsUploading(true)
    try {
      if (eligibleFiles.length === 1 || onMultiFileSelect == null) {
        for (const file of eligibleFiles) {
          await onFileSelect(file)
        }
      } else {
        await onMultiFileSelect(eligibleFiles)
      }
      onOpenChange(false)
    } finally {
      setIsUploading(false)
    }
  }, [assessments, onFileSelect, onMultiFileSelect, onOpenChange])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files
      if (fileList && fileList.length > 0) {
        void assessAndReviewFiles(Array.from(fileList))
      }
      event.target.value = ''
    },
    [assessAndReviewFiles]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const fileList = event.dataTransfer.files
      if (fileList && fileList.length > 0) {
        void assessAndReviewFiles(Array.from(fileList))
      }
    },
    [assessAndReviewFiles]
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
  }, [])

  const isBusy = isAssessing || isUploading
  const showPickZone = phase === 'pick'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{t('sources.addDialog.title')}</DialogTitle>
          <ProductSourceUploadDialogInstructions
            variant={phase === 'review' ? 'review' : 'pick'}
          />
        </DialogHeader>
        {phase === 'review' && (
          <ProductSourceUploadReviewPanel
            assessments={assessments}
            isAssessing={isAssessing}
            isUploading={isUploading}
            onChooseDifferentFiles={resetDialog}
            onConfirmUpload={() => {
              void handleConfirmUpload()
            }}
          />
        )}
        {showPickZone && (
          <div
            className={cn(
              'flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center',
              'border-muted-foreground/25 hover:border-muted-foreground/50',
              isBusy && 'pointer-events-none opacity-60'
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className='mb-2 size-10 text-muted-foreground' />
            <p className='mb-3 text-sm text-muted-foreground'>
              {t('sources.addDialog.dragDrop')}
            </p>
            <p className='mb-4 text-xs text-muted-foreground'>
              {t('sources.addDialog.orClick')}
            </p>
            <input
              ref={fileInputRef}
              type='file'
              accept={PRODUCT_SOURCE_FILE_ACCEPT}
              multiple
              className='sr-only'
              onChange={handleInputChange}
              disabled={isBusy}
            />
            <Button
              disabled={isBusy}
              variant='secondary'
              size='sm'
              onClick={() => fileInputRef.current?.click()}
            >
              {t('sources.addDialog.selectPdf')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
